import dayjs from 'dayjs';
import { Metrics } from '@elementary/common-ui/types';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import {
  numberWithCommas,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { FeedbackErrorResponse } from 'shared/types';
import { isMutationError } from 'shared/utils/utils';
import { FEEDBACK_NOT_FOUND_ERROR_CODE } from './constants';

type BucketMetric = Metrics & { bucket_start: string };

const hasBucketStart = (metric: Metrics): metric is BucketMetric =>
  !!metric.bucket_start;

export const getBucketMetrics = (metrics: Metrics[]) =>
  metrics
    .filter(hasBucketStart)
    .sort(
      (a, b) =>
        localizeDate(a.bucket_start).valueOf() -
        localizeDate(b.bucket_start).valueOf(),
    );

export const getMetricsWindow = (metrics: Metrics[]) => {
  const starts = metrics.flatMap((metric) => metric.bucket_start ?? []);
  const ends = metrics.flatMap((metric) => metric.bucket_end ?? []);
  return {
    start: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : undefined,
    end: ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : undefined,
  };
};

export const formatFeedbackDate = (value: string) =>
  localizeDate(value).format('MMM D, YYYY LT');

export const formatPointValue = (value?: number | null) =>
  value == null ? '-' : numberWithCommas(value, 2);

export const formatSelectedPoint = (metric: BucketMetric) =>
  `Selected: ${formatFeedbackDate(metric.bucket_start)} (${formatPointValue(
    metric.value,
  )})`;

export const getReduceSensitivityMessage = (
  tableName: string,
  metric: BucketMetric,
) =>
  `The volume test on ${tableName} alerted on ${formatFeedbackDate(
    metric.bucket_start,
  )}: the row count was ${formatPointValue(
    metric.value,
  )}, outside the expected range ${formatPointValue(
    metric.min_value,
  )}–${formatPointValue(
    metric.max_value,
  )}. Changes of this size are normal for this table. Suggest a configuration change that makes this test less sensitive to changes like this, and show me the change before applying it.`;

export const getFeedbackErrorResponse = (
  error: unknown,
): FeedbackErrorResponse | undefined =>
  isMutationError(error) ? error.response?.data : undefined;

export const isFeedbackNotFoundError = (error: unknown) =>
  isMutationError(error) &&
  (error.response?.status === 404 ||
    error.response?.data?.elementary_error_code ===
      FEEDBACK_NOT_FOUND_ERROR_CODE);

export const getChangepointMarkerLabel = (
  createdBy: string,
  createdAt: string,
) =>
  `Expected changepoint, added by ${createdBy} on ${formatFeedbackDate(
    createdAt,
  )}`;

export const getReplaceChangepointMessage = (
  createdBy: string,
  createdAt: string,
) =>
  `This point already has a changepoint (added by ${createdBy} on ${formatFeedbackDate(
    createdAt,
  )}). Replace it?`;

export const isSameBucketStart = (a: string, b: string) =>
  dayjs.utc(a).isSame(dayjs.utc(b));

export const getAnotherChangepointMessage = (
  existingCount: number,
  bucketStart: string,
) =>
  `This test already has ${pluralizeString(
    existingCount,
    'changepoint',
    'changepoints',
  )}, marked on the chart. Add another on ${formatFeedbackDate(bucketStart)}?`;

export const getChangepointTrackingProperties = (feedbackId: string) => ({
  feedback_id: feedbackId,
  feedback_type: 'EXPECTED_CHANGE_POINT',
});
